<template>
  <Teleport to="#app">
    <div
      ref="modal"
      class="fixed inset-0 z-30 hidden"
      role="dialog"
      aria-modal="true"
    >
      <!-- Modal Panel -->
      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div
          class="relative z-10 flex items-center justify-center min-h-full p-4 text-center sm:p-0"
        >
          <!-- Backdrop -->
          <div
            ref="modalBackdrop"
            @click="Close"
            class="fixed inset-0 z-0 transition-opacity bg-black/40 backdrop-blur-sm"
          ></div>

          <div
            ref="modalPanel"
            class="relative z-10 w-full max-w-4xl overflow-hidden text-left transition-all duration-300 transform scale-100 bg-white shadow-2xl opacity-100 rounded-2xl sm:my-8"
          >
            <!-- Close Button -->
            <button
              @click="Close"
              class="absolute z-20 p-2 transition-colors rounded-full top-4 right-4 bg-white/80 hover:bg-gray-100"
            >
              <svg
                class="w-6 h-6 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                ></path>
              </svg>
            </button>

            <div v-if="product_data != null" class="flex flex-col md:flex-row">
              <!-- Left: Image Gallery -->
              <div
                class="flex flex-col items-center justify-center w-full p-6 md:w-1/2 bg-gray-50 md:p-8"
              >
                <!-- Main Image -->
                <div
                  class="w-full aspect-[4/5] md:aspect-square bg-white rounded-lg overflow-hidden mb-4 relative shadow-sm"
                >
                  <img
                    id="qv-main-img"
                    :src="$ImageUrl(image_list[active_image_index]?.Image)"
                    class="object-cover w-full h-full transition-all duration-300"
                  />
                </div>
                <!-- Thumbnails -->
                <div
                  class="flex justify-center w-full gap-3 overflow-x-auto no-scrollbar"
                  id="qv-thumbnails"
                >
                  <div
                    v-for="(image, image_index) in image_list"
                    :key="`qv-thumb_${image_index}`"
                    @click="active_image_index = image_index"
                    class="w-16 h-16 overflow-hidden transition-colors border border-gray-200 rounded cursor-pointer hover:border-black shrink-0"
                  >
                    <img
                      :src="$ImageUrl(image.Image)"
                      class="object-cover w-full h-full"
                    />
                  </div>
                </div>
              </div>

              <!-- Right: Info & Form -->
              <div class="flex flex-col w-full p-6 md:w-1/2 md:p-10">
                <!-- Product Header -->
                <div class="mb-6">
                  <h3
                    class="mb-2 text-xl font-bold text-gray-900"
                    id="qv-title"
                  >
                    {{ product_data.Title }}
                  </h3>
                  <div class="flex items-center gap-3">
                    <span
                      class="text-xl font-semibold text-primary"
                      id="qv-price"
                      >${{
                        active_stock ? $MoneyFormat(active_stock.SellPrice) : 0
                      }}</span
                    >
                    <span
                      v-if="
                        active_stock &&
                        active_stock.Price != active_stock.SellPrice
                      "
                      class="text-sm text-gray-400 line-through"
                      id="qv-original-price"
                      >{{ $MoneyFormat(active_stock.Price) }}</span
                    >
                    <div id="qv-tags" class="flex gap-1"></div>
                  </div>
                </div>
                <EventTimer ref="EventTimer" :product_data="product_data" />

                <div
                  v-if="
                    (active_stock && active_stock?.StockingDays != 0) ||
                    product_data.Discount.length > 0
                  "
                  class="p-5 mb-8 space-y-4 bg-gray-50 rounded-xl"
                >
                  <!-- Promos -->
                  <div v-if="product_data.Discount.length > 0">
                    <h4
                      class="mb-2 text-xs font-bold tracking-wider text-gray-400 uppercase"
                    >
                      優惠活動
                    </h4>
                    <div class="space-y-2">
                      <template
                        v-for="(
                          discount, discount_index
                        ) in product_data.Discount"
                      >
                        <div
                          :key="`discount_${discount_index}`"
                          v-if="discount.DiscountType == 'P' || discount.DiscountType == 'S'|| discount.DiscountType == 'F'"
                          class="flex items-start gap-2 text-sm text-gray-700"
                        >
                          <svg
                            class="w-5 h-5 text-rose-500 shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                            ></path>
                          </svg>
                          <span>{{ discount.Title }}</span>
                        </div>

                        <div
                          :key="`discount_${discount_index}`"
                          v-if="discount.DiscountType == 'D'"
                          class="flex items-start gap-2 text-sm text-gray-700"
                        >
                          <svg
                            class="w-5 h-5 text-emerald-500 shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M5 13l4 4L19 7"
                            ></path>
                          </svg>
                          <span>{{ discount.Title }}</span>
                        </div>

                        <div
                          :key="`discount_${discount_index}`"
                          v-if="discount.DiscountType == 'G'"
                          class="flex items-start gap-2 text-sm text-gray-700"
                        >
                          <svg
                            class="w-5 h-5 text-gold-600 shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
                            ></path>
                          </svg>
                          <span>{{ discount.Title }}</span>
                        </div>
                      </template>
                    </div>
                  </div>

                  <div
                    v-if="
                      active_stock &&
                      active_stock?.StockingDays != 0 &&
                      product_data.Discount.length > 0
                    "
                    class="w-full border-t border-gray-200"
                  ></div>
                  <!-- Shipping -->
                  <div v-if="active_stock?.StockingDays != 0">
                    <h4
                      class="mb-2 text-xs font-bold tracking-wider text-gray-400 uppercase"
                    >
                      出貨資訊
                    </h4>
                    <div class="flex items-center gap-2 text-sm text-gray-700">
                      <svg
                        class="w-5 h-5 text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                        ></path>
                      </svg>
                      <span
                        >預購商品，預計
                        <span class="font-bold text-black"
                          >{{ active_stock?.StockingDays }}天</span
                        >
                        出貨</span
                      >
                    </div>
                  </div>
                </div>

                <!-- Options Form -->
                <div class="flex-1 mb-5 space-y-5">
                  <!-- Select 1 (e.g., Color) -->
                  <div
                    v-if="
                      !(
                        first_options.length == 1 &&
                        first_options[0].Title == '無'
                      )
                    "
                  >
                    <label
                      class="block text-xs font-medium text-gray-500 mb-1.5"
                      id="label-opt1"
                    >
                      {{ product_data.Option1 }}</label
                    >
                    <select
                      id="select-opt1"
                      v-model="active_options[0]"
                      class="w-full p-3 text-sm transition-all bg-white border border-gray-200 rounded-lg outline-none focus:border-black focus:ring-1 focus:ring-black"
                    >
                      <option value="">請選擇</option>
                      <option
                        v-for="(option, option_index) in first_options"
                        :key="`first_option_${option_index}`"
                        :value="option.ColorID"
                      >
                        {{ option.Title }}
                      </option>
                    </select>
                  </div>

                  <!-- Select 2 (e.g., Size) -->
                  <div
                    v-if="
                      !(
                        second_options.length == 1 &&
                        second_options[0].Title == '無'
                      )
                    "
                  >
                    <label
                      class="block text-xs font-medium text-gray-500 mb-1.5"
                      id="label-opt2"
                    >
                      {{ product_data.Option2 }}</label
                    >
                    <select
                      id="select-opt2"
                      v-model="active_options[1]"
                      class="w-full p-3 text-sm transition-all bg-white border border-gray-200 rounded-lg outline-none focus:border-black focus:ring-1 focus:ring-black"
                    >
                      <option value="">請選擇</option>
                      <option
                        v-for="(option, option_index) in second_options"
                        :key="`second_option_${option_index}`"
                        :value="option.SizeID"
                      >
                        {{ option.Title }}
                      </option>
                    </select>
                  </div>

                  <!-- Quantity (Simple) -->
                  <div>
                    <label
                      class="block text-xs font-medium text-gray-500 mb-1.5"
                      >數量</label
                    >
                    <div
                      class="flex items-center w-32 border border-gray-200 rounded-lg"
                    >
                      <button
                        @click="amount > 1 ? (amount -= 1) : ''"
                        class="flex items-center justify-center w-10 h-10 text-gray-500 hover:bg-gray-50"
                      >
                        -
                      </button>
                      <input
                        type="text"
                        id="qv-qty"
                        :value="amount"
                        readonly=""
                        class="w-full h-10 text-sm text-center border-gray-200 border-x focus:outline-none"
                      />
                      <button
                        @click="
                          amount < active_stock?.Stock ? (amount += 1) : ''
                        "
                        class="flex items-center justify-center w-10 h-10 text-gray-500 hover:bg-gray-50"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Action Buttons -->
                <div class="flex flex-col gap-3 mb-8">
                  <button
                    v-if="
                      active_stock?.Stock > 0 &&
                      time_status != 'end' &&
                      time_status != 'prepare'
                    "
                    @click="AddShopCart"
                    class="w-full bg-primary text-white py-4 rounded-lg font-medium hover:bg-red-800 transition-transform active:scale-[0.99] shadow-lg flex items-center justify-center gap-2"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                      ></path>
                    </svg>
                    加入購物車
                  </button>

                  <button
                    v-else
                    class="w-full bg-zinc-600 text-white py-4 rounded-lg font-medium hover:bg-zinc-700 transition-transform active:scale-[0.99] shadow-lg flex items-center justify-center gap-2 cursor-not-allowed"
                  >
                    {{
                      time_status == "end"
                        ? "已結束販售"
                        : time_status == "prepare"
                        ? "尚未開賣"
                        : "商品已售完"
                    }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { getLocalStorage } from "@/common/cookie";
import { AddToCart } from "@/common/pixel_methods";
import { GTM_AddShopCart } from "@/common/gtm_methods";
import Teleport from "vue2-teleport";
import EventTimer from "./quick_shop_modal/EventTimer.vue";
export default {
  name: "QuickShopModal",
  components: {
    Teleport,
    EventTimer,
  },
  data() {
    return {
      dialog_status: false,
      product_id: -1,
      active_options: ["", ""],
      amount: 1,
      active_image_index: 0,
    };
  },
  methods: {
    OpenModal() {
      // 顯示並執行動畫
      document.querySelector("body").classList.remove("overflow-y-auto");
      document.querySelector("body").classList.add("overflow-y-hidden");
      this.$refs.modal.classList.remove("hidden");
      // 小延遲讓瀏覽器抓到 display 變化，觸發 transition
      this.timer != null ? clearTimeout(this.timer) : "";
      this.timer = setTimeout(() => {
        this.$refs.modalBackdrop.classList.remove("opacity-0");
        this.$refs.modalPanel.classList.remove("scale-95", "opacity-0");
        this.$refs.modalPanel.classList.add("scale-100", "opacity-100");
        if(this.$refs.EventTimer){
          this.$refs.EventTimer.SetTimer();
        }
      }, 10);
    },
    CloseModal() {
      document.querySelector("body").classList.remove("overflow-y-hidden");
      document.querySelector("body").classList.add("overflow-y-auto");
      this.$refs.modalBackdrop.classList.add("opacity-0");
      this.$refs.modalPanel.classList.remove("scale-100", "opacity-100");
      this.$refs.modalPanel.classList.add("scale-95", "opacity-0");
      this.timer != null ? clearTimeout(this.timer) : "";
      this.timer = setTimeout(() => {
        this.$refs.modal.classList.add("hidden");
      }, 300); // 等待動畫結束
    },
    Open(id) {
      this.product_id = id;
      this.GetProductData();
    },
    Close() {
      this.dialog_status = false;
    },
    async GetProductData() {
      let dataParamList = {};

      dataParamList = this.CheckExistProductData(dataParamList);

      if (Object.keys(dataParamList).length > 0) {
        this.$store.commit("SetLoading", 1);
        await this.$store.dispatch("getPageData", dataParamList);
        this.$store.commit("SetLoading", -1);
      }

      const current_product = this.$store.state.product_page_data;
      if (current_product == null || current_product.Status != "Y") {
        this.$store.commit("SetDialog", {
          status: true,
          content: "商品不存在或已下架",
        });
        return;
      }

      this.InitOptions();
      this.dialog_status = true;
    },
    CheckExistProductData(dataParamList) {
      let tmp_dataParamList = { ...dataParamList };
      const storeProductPage = this.$store.state.product_page_data;
      if (storeProductPage == null) {
        tmp_dataParamList.product_page = this.product_id;
      } else if (
        storeProductPage.id != this.product_id &&
        storeProductPage.MetaTitle != this.product_id
      ) {
        tmp_dataParamList.product_page = this.product_id;
      }

      return tmp_dataParamList;
    },
    InitOptions() {
      let available_stocks = this.product_data.Stock.filter(
        (item) => item.Status == "Y",
      );
      this.$set(this.active_options, 0, available_stocks[0].ColorID);
      this.$set(this.active_options, 1, available_stocks[0].SizeID);
    },
    async AddShopCart() {
      // Pixel 加入購物車 AddToCart(product, option, amount)
      const currentStock = this.product_data.Stock.find(
        (item) =>
          item.ColorID == this.active_options[0] &&
          item.SizeID == this.active_options[1],
      );

            // 檢查目前商品的溫層，並檢查購物車種是否有其他溫層的商品存在
      const otherTempType = this.product_data.DeliveryFrozen == "Y"?'normal':'frozen';
      const ifShopcartHasOtherTemp = await this.$store.dispatch("shopcart_module/CheckShopCartTempType",otherTempType);
      if(ifShopcartHasOtherTemp){
        // 如果購物車中有其他溫層的商品，則提示用戶並返回
        this.$store.commit('SetDialog',{status:true, content:"冷凍商品不能與一般商品一起結帳<br/>您的購物車中已有不同溫層的商品，請先結帳或清空購物車後再加入此商品。"})
        return;
      }



      AddToCart(this.product_data, this.active_options, this.amount);
      // GA 加入購物車
      GTM_AddShopCart(
        {
          ...this.product_data,
          stock: currentStock,
          active_option: this.active_options,
        },
        this.amount,
      );

      const shop_cart_item = {
        product: JSON.parse(JSON.stringify(this.product_data)),
        options: JSON.parse(JSON.stringify(this.active_options)),
        amount: this.amount,
        show_message: true,
        order_memo: this.$route.query.order_memo,
      };
      if (getLocalStorage("account_token")) {
        await this.$store.dispatch("shopcart_module/AddShopCart", shop_cart_item);
      } else {
        await this.$store.commit("shopcart_module/AddShopCart", shop_cart_item);
      }

      this.$emit("add-cart-callback");
    },
  },
  computed: {
    product_data() {
      return this.$store.state.product_page_data;
    },
    image_list() {
      if (this.product_data && this.product_data.Picture) {
        return this.product_data.Picture;
      }
      return [];
    },
    active_stock() {
      if (this.product_data == null) {
        return null;
      }
      if (
        this.active_options.length < 2 ||
        this.active_options.indexOf("") != -1
      ) {
        return null;
      }
      return this.product_data.Stock.find(
        (item) =>
          item.ColorID == this.active_options[0] &&
          item.SizeID == this.active_options[1],
      );
    },
    first_options() {
      let tmp_options = [];
      this.product_data.Stock.forEach((item) => {
        let option = tmp_options.filter(
          (option_item) => option_item.ColorID == item.ColorID,
        );
        if (option.length <= 0 && item.Status == "Y") {
          tmp_options.push({
            ColorID: item.ColorID,
            Title: item.ColorTitle,
          });
        }
      });
      return tmp_options;
    },
    second_options() {
      let tmp_options = [];
      let options = this.product_data.Stock.filter(
        (option_item) =>
          option_item.ColorID == this.active_options[0] &&
          option_item.Status == "Y",
      );
      options.forEach((item) => {
        let option = tmp_options.filter(
          (option_item) => option_item.SizeID == item.SizeID,
        );
        if (option.length <= 0 && item.Status == "Y") {
          tmp_options.push({
            SizeID: item.SizeID,
            Title: item.SizeTitle,
          });
        }
      });
      return tmp_options;
    },
    time_status() {
      if (this.start_time == null || this.end_time == null) {
        return "none";
      }
      let nowTime = new Date().getTime(); // 獲取當前毫秒數
      if (nowTime < this.start_time) {
        // 活動尚未開始
        return "prepare";
      } else if (nowTime > this.start_time && nowTime < this.end_time) {
        // 活動進行中
        return "start";
      } else {
        // 活動結束
        return "end";
      }
    },
    start_time() {
      if (
        this.product_data.GoodsTimeStart == "" ||
        this.product_data.GoodsTimeStart == null
      ) {
        return null;
      }
      let time = new Date();
      time.setFullYear(this.product_data.GoodsTimeStart.slice(0, 4)); // 設定結束年份
      time.setMonth(parseInt(this.product_data.GoodsTimeStart.slice(5, 7)) - 1); //   獲取結束 月份 (從 '0' 開始算)
      time.setDate(parseInt(this.product_data.GoodsTimeStart.slice(8, 10))); //   獲取結束 日
      time.setHours(parseInt(this.product_data.GoodsTimeStart.slice(11, 13))); //   獲取結束 時
      time.setMinutes(parseInt(this.product_data.GoodsTimeStart.slice(14, 16))); //   獲取結束 分
      time.setSeconds(parseInt(this.product_data.GoodsTimeStart.slice(17, 19)));
      return time.getTime();
    },
    end_time() {
      if (
        this.product_data.GoodsTimeEnd == "" ||
        this.product_data.GoodsTimeEnd == null
      ) {
        return null;
      }
      let time = new Date();
      time.setFullYear(this.product_data.GoodsTimeEnd.slice(0, 4)); // 設定結束年份
      time.setMonth(parseInt(this.product_data.GoodsTimeEnd.slice(5, 7)) - 1); //   獲取結束 月份 (從 '0' 開始算)
      time.setDate(parseInt(this.product_data.GoodsTimeEnd.slice(8, 10))); //   獲取結束 日
      time.setHours(parseInt(this.product_data.GoodsTimeEnd.slice(11, 13))); //   獲取結束 時
      time.setMinutes(parseInt(this.product_data.GoodsTimeEnd.slice(14, 16))); //   獲取結束 分
      time.setSeconds(parseInt(this.product_data.GoodsTimeEnd.slice(17, 19)));
      return time.getTime();
    },
  },
  watch: {
    dialog_status() {
      this.dialog_status ? this.OpenModal() : this.CloseModal();
    },
  },
};
</script>
